import { VendorAchAccount, VendorCheckAccount } from '../types';

export interface VendorPaymentAccountOption {
  id: string;
  label: string;
  subtitle: string;
  paymentMethod: 'ACH' | 'CHECK';
  isDefault: boolean;
}

interface VendorPaymentAccountsInput {
  achAccounts?: VendorAchAccount[];
  checkAccounts?: VendorCheckAccount[];
}

const toLast4 = (value?: string) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : digits;
};

const getMaskedAchDisplay = (account: VendorAchAccount) => {
  const maskedValue = account.maskedAccountNumber?.trim();
  const last4 = account.accountNumberLast4 || toLast4(account.accountNumber) || toLast4(maskedValue);

  if (maskedValue) {
    // Some API responses return only placeholders (e.g. "****"). Prefer appending known last4.
    if (/^\*+$/.test(maskedValue) && last4) {
      return `****${last4}`;
    }
    return maskedValue;
  }

  return last4 ? `****${last4}` : '****';
};

const getCheckAddressDisplay = (account: VendorCheckAccount) => {
  const cityStateZip = [account.city, account.state, account.zip].filter(Boolean).join(' ');
  return [account.address, account.addressLine2, cityStateZip, account.country]
    .filter((value) => !!value?.trim())
    .join(', ');
};

export const getSortedAchAccounts = (accounts?: VendorAchAccount[]) => {
  if (!accounts?.length) return [];

  return [...accounts].sort((a, b) => {
    if (a.isDefault === b.isDefault) return 0;
    return a.isDefault ? -1 : 1;
  });
};

export const getSortedCheckAccounts = (accounts?: VendorCheckAccount[]) => {
  if (!accounts?.length) return [];

  return [...accounts].sort((a, b) => {
    if (a.isDefault === b.isDefault) return 0;
    return a.isDefault ? -1 : 1;
  });
};

export const getVendorPaymentAccountOptions = (
  vendor: VendorPaymentAccountsInput | null | undefined,
  paymentMethod?: string | null,
): VendorPaymentAccountOption[] => {
  if (!vendor || !paymentMethod) return [];

  const normalizedMethod = paymentMethod.trim().toUpperCase();

  if (normalizedMethod === 'ACH') {
    const accounts = vendor.achAccounts;
    if (!accounts?.length) return [];

    return getSortedAchAccounts(accounts).map((account) => ({
      id: String(account.id),
      label: account.nickname,
      subtitle: `${account.accountType} ${getMaskedAchDisplay(account)}`,
      paymentMethod: 'ACH',
      isDefault: account.isDefault,
    }));
  }

  if (normalizedMethod === 'CHECK') {
    const accounts = vendor.checkAccounts;
    if (!accounts?.length) return [];

    return getSortedCheckAccounts(accounts).map((account) => ({
      id: String(account.id),
      label: account.nickname,
      subtitle: getCheckAddressDisplay(account),
      paymentMethod: 'CHECK',
      isDefault: account.isDefault,
    }));
  }

  return [];
};

export const getVendorDefaultPaymentAccountId = (
  vendor: VendorPaymentAccountsInput | null | undefined,
  paymentMethod?: string | null,
): string => {
  const options = getVendorPaymentAccountOptions(vendor, paymentMethod);
  return options.find((item) => item.isDefault)?.id || options[0]?.id || '';
};
